import { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useActivities } from '@/hooks/useActivities';
import { useHomePageSettings } from '@/hooks/useHomePageContent';
import { optimizedImageUrl } from '@/lib/storage';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const TITLES_AR = [
  'استكشف',
  'لا تفوّت الفرصة',
  'جرّب شيئاً جديداً',
  'لحظات تستحق التجربة',
  'أنشطة بانتظارك',
  'تجربة اليوم',
  'اكتشف ما يناسبك',
];
const TITLES_EN = [
  'Explore',
  "Don't Miss Out",
  'Try Something New',
  'Moments Worth Living',
  'Activities Await',
  "Today's Experience",
  'Discover What Fits You',
];

type Slide = {
  id: string;
  title: string;
  image: string;
  video?: string | null;
};

const VIDEO_DURATION_MS = 5000;

export function HeroHybrid() {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const { data: activities = [] } = useActivities();
  const { data: settings } = useHomePageSettings();

  const heroVideoIds: string[] = useMemo(() => {
    const arr = (settings as any)?.hero_video_activity_ids as string[] | undefined;
    if (arr && arr.length > 0) return arr;
    const single = (settings as any)?.hero_video_activity_id as string | null | undefined;
    return single ? [single] : [];
  }, [settings]);

  const slides: Slide[] = useMemo(() => {
    const byId = new Map(activities.map((a: any) => [a.id, a]));
    // Featured videos first (in admin-defined order)
    const featured: Slide[] = heroVideoIds
      .map((id) => byId.get(id))
      .filter((a: any) => a && a.video_url)
      .map((a: any) => ({
        id: a.id,
        title: isArabic ? a.title_ar : (a.title_en || a.title_ar),
        image: a.gallery_urls?.[0] || '',
        video: a.video_url,
      }));
    return featured;
  }, [activities, isArabic, heroVideoIds]);

  const [index, setIndex] = useState(0);

  useEffect(() => { setIndex(0); }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setTimeout(() => setIndex((i) => (i + 1) % slides.length), VIDEO_DURATION_MS);
    return () => clearTimeout(t);
  }, [slides, index]);

  if (slides.length === 0) return null;

  const current = slides[index];
  const titles = isArabic ? TITLES_AR : TITLES_EN;
  const rotatingTitle = titles[index % titles.length];

  return (
    <section className="relative w-full overflow-hidden -mt-16 md:-mt-20">
      <div className="relative aspect-[3/4] sm:aspect-[16/9] md:aspect-[21/9] w-full">
        {slides.map((s, i) =>
          s.video ? (
            <video
              key={s.id}
              src={s.video}
              poster={s.image ? optimizedImageUrl(s.image, { width: 1600, quality: 75 }) : undefined}
              autoPlay={i === index}
              muted
              playsInline
              loop
              preload={i === index ? 'auto' : 'none'}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                i === index ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ) : (
            <img
              key={s.id}
              src={optimizedImageUrl(s.image, { width: 1600, quality: 75 })}
              alt={s.title}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                i === index ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ),
        )}

        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

        <div className="absolute inset-0 flex flex-col justify-end px-6 pb-10 md:px-12 md:pb-16 pt-20 md:pt-24">
          <div className="max-w-2xl space-y-4">
            <h2 className="text-3xl md:text-6xl font-extrabold text-white leading-tight drop-shadow-lg">
              {rotatingTitle}
            </h2>
            <p className="text-sm md:text-lg text-white/90 drop-shadow line-clamp-1">
              {current.title}
            </p>

            <div className="pt-2">
              <Link
                to={`/activities/${current.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-white/90 hover:bg-white backdrop-blur-xl border border-white/60 text-foreground font-bold px-6 py-3 shadow-elevated transition-all hover:scale-105"
              >
                <span>{isArabic ? 'جرّب الآن' : 'Try Now'}</span>
                {isArabic ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              </Link>
            </div>
          </div>

          {slides.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Slide ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === index ? 'w-8 bg-white' : 'w-2 bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
